import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, TareaComentario } from '@/types/database'
import { fetchInChunks } from '@/lib/postgrest-chunks'

/** Hasta cuándo leyó el usuario los comentarios de cada tarea, por id de tarea. */
export type LecturasComentarios = Record<string, string>

export const lecturasComentariosService = {
  /**
   * Lecturas del usuario actual para las tareas pedidas.
   * La RLS de `tarea_comentarios_lecturas` devuelve solo las filas del usuario.
   */
  async listar(
    client: SupabaseClient<Database>,
    tareaIds: string[]
  ): Promise<LecturasComentarios> {
    const filas = await fetchInChunks(tareaIds, async (batch) => {
      const { data, error } = await client
        .from('tarea_comentarios_lecturas')
        .select('tarea_id, leido_hasta')
        .in('tarea_id', batch)

      if (error) throw error
      return data ?? []
    })

    return Object.fromEntries(filas.map((f) => [f.tarea_id, f.leido_hasta]))
  },

  /** Marca leídos los comentarios de la tarea hasta la hora del servidor. */
  async marcarLeidos(client: SupabaseClient<Database>, tareaId: string): Promise<void> {
    const { error } = await client.rpc('marcar_comentarios_leidos', { p_tarea_id: tareaId })
    if (error) throw error
  },
}

/**
 * Tareas cuyo último comentario lo escribió otra persona después de la última lectura del usuario.
 * Una tarea sin lectura cuenta como no leída.
 * Sin las lecturas o sin el email del usuario no marca ninguna.
 */
export const tareasConComentariosSinLeer = (
  ultimos: Record<string, Pick<TareaComentario, 'autor' | 'created_at'>>,
  lecturas: LecturasComentarios | undefined,
  email: string | null | undefined
): Set<string> => {
  const sinLeer = new Set<string>()
  if (!lecturas || !email) return sinLeer

  for (const [tareaId, comentario] of Object.entries(ultimos)) {
    if (comentario.autor === email || !comentario.created_at) continue
    const leidoHasta = lecturas[tareaId]
    if (!leidoHasta || Date.parse(comentario.created_at) > Date.parse(leidoHasta)) {
      sinLeer.add(tareaId)
    }
  }
  return sinLeer
}
